import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Camera, FilePlus, FileText, IdCard, ChevronRight } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtDate, fmtDateTime, MEMBERSHIP_CLS } from "@/lib/format";
import { PageHeader, Badge, Loading, AthletePhoto, LicenseStatusBadge, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { AthleteForm } from "@/components/AthleteForm";
import { useAuth } from "@/context/AuthContext";
import { LicenseRequestDialog } from "@/components/LicenseRequestDialog";

const Info = ({ label, value, mono }) => (
  <div><div className="eyebrow">{label}</div><div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div></div>
);

export default function AthleteDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [edit, setEdit] = useState(false);
  const [req, setReq] = useState(false);
  const [ver, setVer] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/athletes/${id}`).then((r) => setA(r.data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/sectors").then((r) => setSectors(r.data)); }, [id]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/athletes/${id}/${kind}`, fd, { timeout: 120000 });
      toast.success(kind === "photo" ? "Fotografia aggiornata" : "Documento caricato");
      setVer(Date.now());
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!a) return <Loading />;
  const name = `${a.first_name} ${a.last_name}`;
  return (
    <div>
      <Link to="/atleti" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-athletes"><ArrowLeft className="h-4 w-4" />Atleti</Link>
      <PageHeader eyebrow="Scheda atleta" title={`${a.last_name} ${a.first_name}`} subtitle={`${a.club || "ASD/SSD non indicata"} · ${sectors.find((s) => s.id === a.sector_id)?.name || ""}`}>
        <Button variant="outline" onClick={() => setEdit(true)} data-testid="athlete-edit-btn"><Pencil className="mr-2 h-4 w-4" />Modifica</Button>
        <Button onClick={() => setReq(true)} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="athlete-request-license-btn"><IdCard className="mr-2 h-4 w-4" />{user.role === "admin" ? "Inserisci licenza" : "Richiedi licenza"}</Button>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card-surface p-6">
          <AthletePhoto athleteId={a.id} hasPhoto={a.has_photo} name={name} version={ver} className="h-48 w-40" rounded="rounded-xl" />
          <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
            <Camera className="h-4 w-4" />{busy ? "Caricamento…" : a.has_photo ? "Sostituisci foto" : "Carica fotografia"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => upload("photo", e.target.files[0])} data-testid="athlete-photo-input" />
          </label>
          <div className="mt-6"><Badge cls={MEMBERSHIP_CLS[a.membership_status]} testid="athlete-membership-status">Tesseramento {a.membership_status}</Badge></div>
          <div className="mt-1 text-xs text-slate-500">Scadenza tessera {fmtDate(a.membership_expiry)}</div>
        </div>
        <div className="card-surface grid grid-cols-2 gap-5 p-6 lg:col-span-2">
          <Info label="Codice fiscale" value={a.tax_code} mono />
          <Info label="Numero tessera MSP" value={a.membership_number} mono />
          <Info label="Data di nascita" value={fmtDate(a.birth_date)} />
          <Info label="Luogo di nascita" value={a.birth_place} />
          <Info label="Sesso" value={a.sex === "F" ? "Femminile" : "Maschile"} />
          <Info label="ASD/SSD" value={a.club} />
          <Info label="Email" value={a.email} />
          <Info label="Telefono" value={a.phone} />
        </div>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="card-surface p-6 lg:col-span-2">
          <div className="mb-4 font-display text-base font-semibold">Licenze possedute</div>
          {a.licenses.length === 0 ? <Empty>Nessuna licenza. Usa "{user.role === "admin" ? "Inserisci licenza" : "Richiedi licenza"}".</Empty> : (
            <div className="divide-y divide-slate-100">
              {a.licenses.map((l) => (
                <Link key={l.id} to={`/licenze/${l.id}`} className="flex items-center justify-between gap-3 py-3 transition-colors hover:bg-slate-50" data-testid={`athlete-license-${l.id}`}>
                  <div>
                    <div className="font-mono text-sm font-semibold text-slate-900">{l.license_number || "Richiesta in attesa"}</div>
                    <div className="text-xs text-slate-500">Scadenza {fmtDate(l.expiry_date)} · richiesta il {fmtDateTime(l.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2"><LicenseStatusBadge status={l.status} /><ChevronRight className="h-4 w-4 text-slate-400" /></div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="card-surface p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="font-display text-base font-semibold">Documentazione</div>
            <label className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900">
              <FilePlus className="h-4 w-4" />Carica
              <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => upload("documents", e.target.files[0])} data-testid="athlete-document-input" />
            </label>
          </div>
          {a.documents.length === 0 ? <div className="text-sm text-slate-500">Nessun documento caricato (certificato medico, modulo di richiesta…).</div> : (
            <ul className="space-y-2">
              {a.documents.map((d) => (
                <li key={d.id}>
                  <button onClick={() => downloadFile(`/athletes/${a.id}/documents/${d.id}`, d.name).catch((e) => toast.error(errMsg(e)))} className="flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm hover:bg-slate-50" data-testid={`athlete-document-${d.id}`}>
                    <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="flex-1 truncate">{d.name}</span>
                    <span className="text-xs text-slate-400">{fmtDate(d.uploaded_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <AthleteForm open={edit} onOpenChange={setEdit} athlete={a} onSaved={load} />
      <LicenseRequestDialog open={req} onOpenChange={setReq} athlete={a} onDone={load} />
    </div>
  );
}
